import { useSocketStore } from '@/store/socket-store';
import { useGridApiExplorerStore } from '@/modules/marketplace/store/grid-api-explorer-store';
import { IRowNode } from 'ag-grid-enterprise';
import {
  BookOrder,
  BookOrderSchema,
  QueryMarketplaceInstrument,
  QueryMarketplaceInstrumentSchema,
  QueryMarketplaceItem,
  QueryMarketplaceItemSchema,
} from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import {
  JsonObject,
  JsonValue,
  OmsOrderType,
  OrderIntent,
  PbInstrumentInfoSchema,
  clone,
  create,
  fromJson,
  isMessage,
} from '@/connect';
import { store as globalStore } from '@/store/store';
import { useSecuritiesStore } from '@/store/securities-store';
import {
  DataSourceConfig,
  fetchInstruments,
  hasAnyFilters,
} from '@/modules/marketplace/services/datasource-explorer';
import { ref } from 'vue';
import { Security } from '@/modules/common/models';

type OrderKey = 'borrowOrder' | 'lendOrder';

export type EventType = 'VenueOrderAdded' | 'VenueOrderUpdated' | 'VenueOrderRemoved';

interface Target {
  node: IRowNode;
  key: OrderKey;
  order: BookOrder;
}

export function useSocketExplorer(initialDataSourceConfig: DataSourceConfig): {
  setDataConfig: (config: DataSourceConfig) => void;
} {
  const { getGridApi } = useGridApiExplorerStore();

  const socketStore = useSocketStore();
  socketStore.useEventHandler('refreshVenueOrders', refreshVenueOrders);
  socketStore.useEventHandler('refreshAllVenueOrders', refreshAllVenueOrders);
  socketStore.useEventHandler('open', handleSocketReconnect);
  const dataConfig = ref<DataSourceConfig>(initialDataSourceConfig);

  async function refreshVenueOrders(payload: JsonObject) {
    const order = fromJson(BookOrderSchema, payload.order);

    // we're only interested in orders of the currently picked side
    if (
      order.side === dataConfig.value.tradeIntention &&
      order.companyId !== globalStore.state.loginState?.user?.companyDisplayId
    )
      return;

    ensureSecurityInStore(payload.instrument);
    const eventType = getEventType(payload);

    // we are not interested in "MARKET", avoid unecessary re-renders and fetching
    if (order.orderType === OmsOrderType.MARKET) return;

    switch (eventType) {
      case 'VenueOrderAdded':
        await handleAdd(order);
        break;
      case 'VenueOrderRemoved':
        await handleRemove(order);
        break;
      case 'VenueOrderUpdated':
        await handleUpdate(order);
        break;
    }
  }

  function handleSocketReconnect() {
    refreshAllVenueOrders();
  }

  function refreshAllVenueOrders() {
    // causes the screen to flicker when there's a large number of rows
    getGridApi().refreshServerSide({ purge: false });
  }

  async function handleUpdate(order: BookOrder) {
    const target = findTarget(order.rootClientOrderRef);
    if (!target) return;
    if (order.fee === target.order.fee) {
      // when rate is the same, sorting position will remain the same
      // so it's okay to update the order directly
      getGridApi().applyServerSideTransaction({
        route: [order.cusip],
        update: [clone(QueryMarketplaceItemSchema, { ...target.node.data, [target.key]: order })],
      });
    } else {
      // refresh when rate is different, because sorting position can be different
      await refreshCusipOrders(order.cusip);
    }
  }

  async function handleAdd(order: BookOrder) {
    // check if we can find a group for this cusip in our current dataset
    const groupNode = getGridApi().getRowNode(order.cusip);

    // if we can find a group then
    if (groupNode) {
      // we fully refresh the group, because we don't know exactly where to insert our order into the group
      await refreshCusipOrders(order.cusip);
      if (isMatchableOrder(order)) {
        groupNode.data.orderCount++;
        getGridApi().applyServerSideTransaction({ update: [groupNode.data] });
      }
    }
    // if the group doesn't exist, this is its first order for that group
    else {
      // if we have filters being applied we need to determine if this order is visible given the current filters
      if (hasAnyFilters(dataConfig.value)) {
        const config = { ...dataConfig.value };

        // we will query the backend to see if the order should be visible according to our current filters
        // if we're not already filtering on specific instruments then we will apply a instrument filter to the query
        // to be a bit more efficient
        if (!config.securities) {
          const pbInfo = useSecuritiesStore().getSecurity(order.cusip);
          if (pbInfo) {
            config.securities = [Security.fromPbInstrumentInfo(pbInfo)];
          }
        }
        // if the instrument is not part of the filtered dataset, ignore event
        const isInstrumentVisibleWithFilters = (await fetchInstruments(config, null)).some(
          (i) => i.instrument?.cusip === order.cusip
        );
        if (!isInstrumentVisibleWithFilters) {
          return;
        }
      }

      // add the group for the instrument
      const instrument = useSecuritiesStore().getSecurity(order.cusip);
      const sortedTickers = [instrument?.ticker, ...getSortedTickers()].sort();
      const addIndex = sortedTickers.indexOf(instrument?.ticker);
      getGridApi().applyServerSideTransaction({
        add: [
          create(QueryMarketplaceInstrumentSchema, {
            instrument,
            orderCount: isMatchableOrder(order) ? 1n : 0n,
          }),
        ],
        addIndex,
      });
      // after we add, AgGrid will automatically fetch orders if the group is expanded
    }
  }

  async function handleRemove(order: BookOrder) {
    await refreshCusipOrders(order.cusip);

    const groupNode = getGridApi().getRowNode(order.cusip);
    if (!groupNode) return;

    if (noOrdersOfCusipLeft(order.cusip)) {
      getGridApi().applyServerSideTransaction({ remove: [groupNode.data] });
    } else if (isMatchableOrder(order)) {
      groupNode.data.orderCount--;
      getGridApi().applyServerSideTransaction({ update: [groupNode.data] });
    }
  }

  // async helper to refresh the orders for a given cusip
  // once it's finished, we know we have the latest orders for that cusip
  function refreshCusipOrders(cusip: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const onModelUpdated = () => {
        getGridApi().removeEventListener('modelUpdated', onModelUpdated);
        resolve();
      };
      getGridApi().addEventListener('modelUpdated', onModelUpdated);
      getGridApi().refreshServerSide({ route: [cusip], purge: false });
    });
  }

  function findTarget(orderRef: string): Target | null {
    for (const node of getAllMarketplaceItemNodes()) {
      let key: OrderKey | null = null;
      if (node.data?.lendOrder?.rootClientOrderRef === orderRef) key = 'lendOrder';
      if (node.data?.borrowOrder?.rootClientOrderRef === orderRef) key = 'borrowOrder';
      if (key) return { node, key, order: node.data?.[key] as BookOrder };
    }
    return null;
  }

  function getSortedTickers(): string[] {
    const tickers: string[] = [];

    getGridApi().forEachNode(
      (node: IRowNode<QueryMarketplaceItem | QueryMarketplaceInstrument>) => {
        if (
          isMessage(node.data, QueryMarketplaceInstrumentSchema) &&
          node.data.instrument?.ticker
        ) {
          tickers.push(node.data.instrument.ticker);
        }
      }
    );

    return tickers.sort();
  }

  // only return QueryMarketplaceItem, ignore QueryMarketplaceInstrument (because they are node.data groups)
  function getAllMarketplaceItemNodes() {
    const items: Array<IRowNode<QueryMarketplaceItem>> = [];

    getGridApi().forEachNode(
      (node: IRowNode<QueryMarketplaceItem | QueryMarketplaceInstrument>) => {
        if (isMessage(node.data, QueryMarketplaceItemSchema)) {
          items.push(node as IRowNode<QueryMarketplaceItem>);
        }
      }
    );
    return items;
  }

  function noOrdersOfCusipLeft(cusip: string): boolean {
    return !getAllMarketplaceItemNodes().some(
      (node) => node.data?.borrowOrder?.cusip === cusip || node.data?.lendOrder?.cusip === cusip
    );
  }

  function isMatchableOrder(order: BookOrder) {
    return (
      order.orderIntent === OrderIntent.FIRM &&
      order.companyId !== globalStore.state.loginState.user?.companyDisplayId
    );
  }

  function ensureSecurityInStore(payload: JsonValue) {
    assertInstrumentNotNull(payload);
    const instrument = create(PbInstrumentInfoSchema, {
      id: payload.id as string,
      cusip: payload.cusip as string,
      name: payload.name as string,
      ticker: payload.ticker as string,
      lastClosePrice: payload.last_close_price as string,
    });
    useSecuritiesStore().addSecurities({ [instrument.cusip]: instrument });
  }

  function assertInstrumentNotNull(instrument: JsonValue): asserts instrument is JsonObject {
    if (!instrument) throw new Error('Instrument is null');
  }

  function getEventType(payload: JsonObject): EventType {
    switch (payload.eventType) {
      case 'VenueOrderAdded':
      case 'VenueOrderUpdated':
      case 'VenueOrderRemoved':
        return payload.eventType;
      default:
        throw new Error(`Unknown event type: ${payload.eventType}`);
    }
  }

  return {
    setDataConfig: (config: DataSourceConfig) => {
      dataConfig.value = config;
    },
  };
}
